import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2, Search, UserX, Undo2, Calendar, ArrowLeft, ArrowRight, Users, DollarSign, AlertCircle, AlertTriangle, ShieldAlert, FileText, Phone, Upload } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import api, { handleApiError, formatCurrency, formatDate, brTodayStr, brMonthStr, brShiftMonth, brCompetencyOf } from "@/lib/api";
import { toast } from "sonner";
import { usePermission } from "@/hooks/usePermission";
import { useAuth } from "@/context/AuthContext";
import EmployeeFormDialog from "./EmployeeFormDialog";
import EmployeeImportCnabDialog from "./EmployeeImportCnabDialog";
import { DeleteEmployeeDialog, TerminateEmployeeDialog, UndoTerminateDialog } from "./EmployeeActionDialogs";
import EmployeeStatement from "./EmployeeStatement";
import Pagination from "@/components/Pagination";
import { isBankAccountFormat, isBankAgencyFormat, isCompeFormat } from "@/data/banks";

const MONTHS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const emptyForm = { type: "PF", name: "", email: "", cpf_cnpj: "", salary: "", hire_date: "", approval_required: null, send_notification: false, phone: "", department: "", socio_name: "", socio_cpf: "", has_retention: false, retention_pct: "", bank_code: "", bank_agency: "", bank_account: "", bank_type: "", bank_key_pix: "", bank_key_pix_type: "", payment_method: "pix", custody_override: "inherit" };
const isCiphertext = (value) => typeof value === "string" && value.startsWith("enc:");
const safeField = (value) => isCiphertext(value) ? "" : (value || "");

const isTerminated = (emp) => !!emp.termination_date;
const isTerminatedOver2Months = (emp) => {
  if (!emp.termination_date) return false;
  // Compare against cutoff in BR calendar time (matches backend semantics).
  return emp.termination_date < brShiftMonth(brTodayStr(), -2);
};
const canUndoTerminate = (emp) => {
  if (!emp.termination_date) return false;
  return emp.termination_date.substring(0, 7) >= brMonthStr();
};

export default function CompanyEmployees() {
  const brMonth = brMonthStr(); // "YYYY-MM" in BRT
  const [year, setYear] = useState(Number(brMonth.split("-")[0]));
  const [month, setMonth] = useState(Number(brMonth.split("-")[1]));
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [terminateDialog, setTerminateDialog] = useState(null);
  const [undoDialog, setUndoDialog] = useState(null);
  const [statementEmployee, setStatementEmployee] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const { canEdit } = usePermission("colaboradores");
  const { user } = useAuth();
  const isMaster = user?.is_master;
  const [empPage, setEmpPage] = useState(1);

  const monthStr = `${year}-${String(month).padStart(2, "0")}`;
  const isCurrent = monthStr === brMonthStr();

  const fetchMonthlyData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ month: monthStr, page: empPage, page_size: 20 });
    if (search) params.set("search", search);
    if (deptFilter !== "all") params.set("department", deptFilter);
    if (typeFilter !== "all") params.set("type", typeFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (sortField) { params.set("sort_by", sortField); params.set("sort_dir", sortDir); }
    try { const res = await api.get(`/employees/monthly?${params}`); setMonthlyData(res.data); } catch (err) { toast.error(handleApiError(err, "Erro ao carregar colaboradores")); }
    setLoading(false);
  }, [monthStr, empPage, search, deptFilter, typeFilter, statusFilter, sortField, sortDir]);

  useEffect(() => { fetchMonthlyData(); }, [fetchMonthlyData]);

  useEffect(() => {
    api.get("/departments").then(res => setDepartments(res.data || [])).catch(() => {});
  }, []);

  const prevMonth = () => { setEmpPage(1); if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { setEmpPage(1); if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  const employees = monthlyData?.employees || [];
  const summary = monthlyData?.summary || {};
  const empPagination = monthlyData?.pagination || null;
  const isEditable = monthlyData?.is_editable ?? false;
  const lockReason = monthlyData?.lock_reason;

  const deptOptions = [...new Set(employees.map(e => e.department || "Sem departamento"))].sort();

  // Data comes pre-filtered and sorted from backend
  const filtered = employees;

  // `prefill` = formulário pré-preenchido EM MEMÓRIA pelo item `pendente` da
  // importação por CNAB (SPEC-ONBOARD-CNAB §6); sem URL, storage nem log.
  const openCreate = (prefill = {}) => { setForm({ ...emptyForm, ...prefill }); setEditingId(null); setEditingEmployee(null); setDialogOpen(true); };
  const openEdit = (emp) => {
    setForm({ type: emp.type, name: emp.name, email: emp.email, cpf_cnpj: safeField(emp.cpf_cnpj), salary: String(emp.salary), hire_date: emp.hire_date || "", approval_required: emp.approval_required, phone: safeField(emp.phone), department: emp.department || "", socio_name: emp.socio_name || "", socio_cpf: safeField(emp.socio_cpf), has_retention: emp.has_retention || false, retention_pct: emp.retention_pct != null ? String(emp.retention_pct) : "", bank_code: emp.bank_code || "", bank_agency: safeField(emp.bank_agency), bank_account: safeField(emp.bank_account), bank_type: emp.bank_type || "", bank_key_pix: safeField(emp.bank_key_pix), bank_key_pix_type: emp.bank_key_pix_type || "", payment_method: emp.payment_method || "bank", custody_override: emp.custody_override || "inherit" });
    setEditingId(emp.id);
    setEditingEmployee(emp);
    setDialogOpen(true);
  };

  // Espelho do backend `services/employee_service.update_employee`: a trava é
  // por competência BR (mês de cadastro em Brasília × mês corrente em
  // Brasília), nunca pelo fuso do navegador nem pelo mês do timestamp UTC.
  const isFieldLocked = () => {
    if (!editingEmployee?.created_at) return false;
    const createdMonth = brCompetencyOf(editingEmployee.created_at);
    if (!createdMonth) return false;
    return createdMonth < brMonthStr();
  };

  const handleSave = async () => {
    const missing = [];
    if (!editingId) {
      if (!form.name.trim()) missing.push(form.type === "PF" ? "Nome Completo" : "Razão Social");
      if (!form.cpf_cnpj.trim()) missing.push(form.type === "PF" ? "CPF" : "CNPJ");
      if (!form.salary || parseFloat(form.salary) <= 0) missing.push("Salário mensal");
      if (!form.phone.trim()) missing.push("Celular");
      if (!form.department || !form.department.trim()) missing.push("Departamento");
      if (form.type === "PJ") { if (!form.socio_name.trim()) missing.push("Nome do Sócio"); if (!form.socio_cpf.trim()) missing.push("CPF do Sócio"); }
    }
    if (!form.payment_method) missing.push("Meio de pagamento");
    if (form.payment_method === "pix" && !form.cpf_cnpj.trim()) missing.push("CPF/CNPJ para chave PIX");
    if (form.payment_method !== "pix") {
      if (!form.bank_code) missing.push("Banco");
      else if (!isCompeFormat(form.bank_code)) missing.push("Banco (use o código COMPE de 3 dígitos)");
      // Gate de save com espelho EXATO do backend (A-R3e-xv). Vazio e fora do
      // formato são estados DISTINTOS, com mensagens distintas: o primeiro é
      // cadastro incompleto, o segundo é um valor que o schema recusaria com um
      // 422 e mandaria o operador procurar o campo errado.
      if (!form.bank_agency) missing.push("Agência");
      else if (!isBankAgencyFormat(form.bank_agency)) missing.push("Agência de 1 a 5 dígitos, sem o dígito verificador (ex.: 0001)");
      if (!form.bank_account) missing.push("Conta");
      else if (!isBankAccountFormat(form.bank_account)) missing.push("Conta com até 12 dígitos e o dígito verificador após um único hífen (ex.: 12345-6)");
      if (!form.bank_type) missing.push("Tipo de Conta");
    }
    if (missing.length) { toast.error(`Campos obrigatórios: ${missing.join(", ")}`); return; }
    setSaving(true);
    try {
      const payload = { ...form, salary: parseFloat(form.salary) || 0, retention_pct: form.has_retention && form.retention_pct ? parseFloat(form.retention_pct) : null };
      Object.keys(payload).forEach((key) => { if (isCiphertext(payload[key])) delete payload[key]; });
      ["cpf_cnpj", "socio_cpf", "bank_agency", "bank_account", "bank_key_pix"].forEach((key) => {
        if (editingId && isCiphertext(editingEmployee?.[key]) && payload[key] === "") delete payload[key];
      });
      if (payload.department === "__custom__") payload.department = "";
      if (payload.payment_method === "pix") {
        const digits = (payload.cpf_cnpj || "").replace(/\D/g, "");
        payload.bank_key_pix = digits;
        payload.bank_key_pix_type = "cpf_cnpj";
      } else {
        // TRIM é a única transformação permitida (o backend faz o mesmo
        // `strip()`), e é exatamente o valor que o gate acima validou. O
        // `formatBankAccount` que morava aqui reescrevia o que o operador
        // digitou — `replace(/\D/g,"")` + reinterpretar o último dígito como DV
        // — e mandava ao servidor uma conta que ninguém viu na tela.
        // `in` e não `?? ""`: o bloco de ciphertext acima DELETA a chave quando
        // o campo ficou vazio sobre um valor cifrado — reintroduzi-la como ""
        // apagaria o dado bancário gravado.
        ["bank_agency", "bank_account"].forEach((k) => {
          if (k in payload) payload[k] = String(payload[k] ?? "").trim();
        });
      }
      if (editingId) { await api.put(`/employees/${editingId}`, payload); toast.success(`${form.name} atualizado com sucesso`); }
      else { await api.post("/employees", payload); toast.success(`${form.name} cadastrado com sucesso`); }
      setDialogOpen(false);
      fetchMonthlyData();
    } catch (err) { toast.error(handleApiError(err, "Erro ao salvar")); }
    setSaving(false);
  };

  // O diálogo da importação fica FORA do ramo de loading, numa posição estável:
  // o `fetchMonthlyData` depois do aplicar (ou do cadastro manual de um
  // pendente) liga o spinner da página inteira, e o diálogo não pode desmontar
  // e perder o resultado que está na memória dele.
  const importDialog = canEdit ? (
    <EmployeeImportCnabDialog key="import-cnab" open={importOpen} onOpenChange={setImportOpen}
      onCadastrarManual={openCreate} onApplied={fetchMonthlyData} departments={departments} />
  ) : null;

  if (loading) return <><div key="loading" className="flex items-center justify-center h-screen"><div className="loading-spinner w-8 h-8" /></div>{importDialog}</>;

  return (
    <>
    <div key="page" className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-5 lg:space-y-6" data-testid="company-employees">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold tracking-tight">Colaboradores</h1>
          <p className="text-sm text-muted-foreground mt-1">Variação mensal de colaboradores e folha salarial</p>
        </div>
        <div className="flex flex-wrap gap-2 self-start">
          {isEditable && canEdit && (
            <Button data-testid="import-cnab-btn" variant="outline" onClick={() => setImportOpen(true)} className="border-white/10 font-semibold">
              <Upload className="w-4 h-4 mr-2" /> Importar último CNAB
            </Button>
          )}
          {isEditable && canEdit && (
            <Button data-testid="add-employee-btn" onClick={() => openCreate()} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
              <Plus className="w-4 h-4 mr-2" /> Novo Colaborador
            </Button>
          )}
        </div>
      </div>

      <Card className="border-white/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="emp-prev-month-btn"><ArrowLeft className="w-4 h-4" /></Button>
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-blue-400" />
              <span className="text-lg font-semibold" data-testid="emp-month-label">{MONTHS[month - 1]} / {year}</span>
              {isCurrent && <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-yellow-500/15 text-yellow-400"><AlertCircle className="w-3 h-3" /> Mês atual</span>}
              {isEditable && !isCurrent && <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-blue-500/15 text-blue-400"><AlertCircle className="w-3 h-3" /> Editável</span>}
              {!isEditable && !isCurrent && <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-muted text-muted-foreground">Bloqueado</span>}
              {!isEditable && lockReason && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-red-500/10 text-red-400 border border-red-500/20 cursor-help" data-testid="not-editable-tag">
                        <ShieldAlert className="w-3 h-3" /> Não editável
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-xs">
                      {lockReason === "closed"
                        ? "Este mês já foi fechado. Reabra o fechamento para editar."
                        : "Existe um fechamento em mês posterior a este. Reabra o mês mais recente primeiro para desbloquear a edição."}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="emp-next-month-btn"><ArrowRight className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Card className="border-white/5 card-interactive"><CardContent className="p-5"><div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-blue-500/10"><Users className="w-4 h-4 text-blue-400" /></div><div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Colaboradores</p><p className="text-xl font-bold" data-testid="emp-summary-count">{summary.employee_count || 0}</p></div></div></CardContent></Card>
        <Card className="border-white/5 card-interactive"><CardContent className="p-5"><div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-emerald-500/10"><DollarSign className="w-4 h-4 text-emerald-400" /></div><div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Folha Salarial</p><p className="text-xl font-bold text-emerald-400" data-testid="emp-summary-salary">{formatCurrency(summary.total_salary || 0)}</p></div></div></CardContent></Card>
        <Card className="border-white/5 card-interactive"><CardContent className="p-5"><div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-purple-500/10"><DollarSign className="w-4 h-4 text-purple-400" /></div><div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Folha Total (Sal + Ben)</p><p className="text-xl font-bold text-purple-400" data-testid="emp-summary-payroll">{formatCurrency(summary.total_payroll || 0)}</p></div></div></CardContent></Card>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="search-employees" placeholder="Buscar colaborador..." value={search} onChange={(e) => { setSearch(e.target.value); setEmpPage(1); }} className="pl-9 h-9 bg-black/20 border-white/10 text-xs" />
        </div>
        <Select value={deptFilter} onValueChange={(v) => { setDeptFilter(v); setEmpPage(1); }}>
          <SelectTrigger className="w-[160px] h-9 bg-black/20 border-white/10 text-xs"><SelectValue placeholder="Departamento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os departamentos</SelectItem>
            {deptOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setEmpPage(1); }}>
          <SelectTrigger className="w-[100px] h-9 bg-black/20 border-white/10 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">PF e PJ</SelectItem>
            <SelectItem value="PF">PF</SelectItem>
            <SelectItem value="PJ">PJ</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setEmpPage(1); }}>
          <SelectTrigger className="w-[110px] h-9 bg-black/20 border-white/10 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="terminated">Rescindidos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-white/5">
        <CardContent className="p-0 overflow-x-auto">
          <Table className="min-w-[700px] [&_td]:px-3 [&_td]:py-2.5 [&_th]:px-3 [&_th]:py-2.5">
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                {[
                  { key: "name", label: "Nome" },
                  { key: "department", label: "Departamento" },
                  { key: "type", label: "Tipo" },
                  { key: "salary", label: "Salário" },
                  { key: "hire_date", label: "Contratação" },
                ].map(col => (
                  <TableHead key={col.key}
                    className="text-xs uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none"
                    onClick={() => {
                      if (sortField === col.key) setSortDir(sortDir === "asc" ? "desc" : "asc");
                      else { setSortField(col.key); setSortDir(col.key === "name" || col.key === "hire_date" || col.key === "department" ? "asc" : "desc"); }
                    }}>
                    {col.label} {sortField === col.key && (sortDir === "asc" ? "↑" : "↓")}
                  </TableHead>
                ))}
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Nenhum colaborador encontrado neste período</TableCell></TableRow>
              ) : filtered.map((emp) => {
                const dimmed = isTerminatedOver2Months(emp);
                const term = isTerminated(emp);
                return (
                  <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`} className={`border-white/5 hover:bg-white/[0.02] ${dimmed ? "opacity-40" : ""}`}>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <p className={`font-medium ${term ? "text-muted-foreground" : ""}`}>{emp.name}</p>
                          <TooltipProvider>
                            {emp.first_login && (
                              <Tooltip><TooltipTrigger><ShieldAlert className="w-3 h-3 text-orange-400" data-testid={`badge-profile-incomplete-${emp.id}`} /></TooltipTrigger>
                              <TooltipContent><p className="text-xs">Perfil incompleto</p></TooltipContent></Tooltip>
                            )}
                            {emp.payment_method !== "pix" && !emp.bank_code && (
                              <Tooltip><TooltipTrigger><AlertTriangle className="w-3 h-3 text-yellow-400" data-testid={`badge-no-bank-${emp.id}`} /></TooltipTrigger>
                              <TooltipContent><p className="text-xs">Sem dados bancários</p></TooltipContent></Tooltip>
                            )}
                            {emp.payment_method !== "pix" && emp.bank_code && !isCompeFormat(emp.bank_code) && (
                              <Tooltip><TooltipTrigger><AlertCircle className="w-3 h-3 text-red-400" data-testid={`badge-bad-bank-${emp.id}`} /></TooltipTrigger>
                              <TooltipContent><p className="text-xs">Código de banco inválido — precisa ser COMPE de 3 dígitos</p></TooltipContent></Tooltip>
                            )}
                            {!emp.phone && (
                              <Tooltip><TooltipTrigger><Phone className="w-3 h-3 text-red-400" data-testid={`badge-no-phone-${emp.id}`} /></TooltipTrigger>
                              <TooltipContent><p className="text-xs">Sem telefone</p></TooltipContent></Tooltip>
                            )}
                          </TooltipProvider>
                        </div>
                        {emp.type === "PJ" && emp.socio_name && <p className="text-xs text-muted-foreground">Sócio: {emp.socio_name}</p>}
                        <p className="text-xs text-muted-foreground">{emp.email}</p>
                      </div>
                    </TableCell>
                    <TableCell><span className="text-sm text-muted-foreground max-w-[120px] truncate block" title={emp.department || ""} data-testid={`emp-department-${emp.id}`}>{emp.department || "-"}</span></TableCell>
                    <TableCell><span className={`px-2 py-0.5 rounded text-xs font-semibold ${emp.type === "PJ" ? "bg-purple-500/15 text-purple-400" : "bg-blue-500/15 text-blue-400"}`}>{emp.type}</span></TableCell>
                    <TableCell className="text-sm">{formatCurrency(emp.salary)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(emp.hire_date)}</TableCell>
                    <TableCell>{term ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-500/15 text-red-400">Rescindido {formatDate(emp.termination_date)}</span> : <span className="px-2 py-0.5 rounded text-xs font-semibold bg-emerald-500/15 text-emerald-400">Ativo</span>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" data-testid={`statement-btn-${emp.id}`}
                          onClick={() => setStatementEmployee(emp)} className="h-8 w-8 text-muted-foreground hover:text-blue-400" title="Extrato">
                          <FileText className="w-3.5 h-3.5" />
                        </Button>
                        {canEdit && (
                          <>
                            {isEditable && term && canUndoTerminate(emp) && <Button variant="ghost" size="icon" data-testid={`undo-terminate-btn-${emp.id}`} onClick={() => setUndoDialog(emp.id)} className="h-8 w-8 text-muted-foreground hover:text-emerald-400" title="Desfazer rescisão"><Undo2 className="w-3.5 h-3.5" /></Button>}
                            {isEditable && !term && <Button variant="ghost" size="icon" data-testid={`terminate-employee-btn-${emp.id}`} onClick={() => setTerminateDialog(emp.id)} className="h-8 w-8 text-muted-foreground hover:text-orange-400" title="Rescindir"><UserX className="w-3.5 h-3.5" /></Button>}
                            <Button variant="ghost" size="icon" data-testid={`edit-employee-btn-${emp.id}`} onClick={() => openEdit(emp)} className="h-8 w-8 text-muted-foreground hover:text-foreground" title="Editar colaborador"><Edit className="w-3.5 h-3.5" /></Button>
                            {isEditable && isMaster && <Button variant="ghost" size="icon" data-testid={`delete-employee-btn-${emp.id}`} onClick={() => setDeleteDialog(emp.id)} className="h-8 w-8 text-muted-foreground hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {empPagination && <Pagination page={empPagination.page} totalPages={empPagination.total_pages}
        total={empPagination.total} pageSize={empPagination.page_size} onPageChange={setEmpPage} />}

      <EmployeeFormDialog open={dialogOpen} onOpenChange={setDialogOpen} form={form} setForm={setForm}
        editingId={editingId} editingEmployee={editingEmployee} isFieldLocked={isFieldLocked} onSave={handleSave} saving={saving} departments={departments} />

      <DeleteEmployeeDialog employeeId={deleteDialog} employeeName={employees.find(e => e.id === deleteDialog)?.name} open={!!deleteDialog}
        onOpenChange={(v) => { if (!v) setDeleteDialog(null); }} onSuccess={fetchMonthlyData}
        onTerminateInstead={() => setTerminateDialog(deleteDialog)} />

      <TerminateEmployeeDialog employeeId={terminateDialog} employeeName={employees.find(e => e.id === terminateDialog)?.name} open={!!terminateDialog}
        onOpenChange={(v) => { if (!v) setTerminateDialog(null); }} onSuccess={fetchMonthlyData} />

      <UndoTerminateDialog employeeId={undoDialog} employeeName={employees.find(e => e.id === undoDialog)?.name} open={!!undoDialog}
        onOpenChange={(v) => { if (!v) setUndoDialog(null); }} onSuccess={fetchMonthlyData} />

      <EmployeeStatement
        employeeId={statementEmployee?.id}
        employeeName={statementEmployee?.name}
        open={!!statementEmployee}
        onClose={(open) => { if (!open) setStatementEmployee(null); }} />
    </div>
    {importDialog}
    </>
  );
}
